import { defineStore } from 'pinia'

/**
 * Shared ComboEditor Store
 *
 * This store contains all the logic for editing combos.
 * It can be used by both commerce store (regular POS) and checkCart store (check/table service).
 *
 * Benefits:
 * - Single source of truth for combo editing logic
 * - No duplication between stores
 * - Easy to test and maintain
 * - Consistent behavior across POS and checks
 */
export const useComboEditorStore = defineStore('comboEditor', () => {
  const log = useLogger('store:comboEditor')

  // ============================================
  // STATE
  // ============================================
  const comboStaged = ref<Record<string, unknown>>({})
  const comboModal = ref<boolean>(false)
  const comboCustomized = ref<boolean>(false)
  const comboCustomOptions = ref<Record<string, unknown> | null>(null)

  // ============================================
  // COMPUTED
  // ============================================
  const comboStagedTotal = computed((): number => {
    if (!comboStaged.value?.quantity || !comboStaged.value?.price) {
      return 0
    }

    const baseTotal = (comboStaged.value.quantity as number) * (comboStaged.value.price as number)
    comboStaged.value.total = baseTotal

    return baseTotal
  })

  const processedComboOptions = computed(() => {
    const items = comboCustomOptions.value?.items

    if (!items) {
      return {
        selected: [],
        itemsNeeded: 0,
        total: 0,
      }
    }

    try {
      // Deep clone selected items to ensure modifiers are preserved
      const selected = (items as any[]).map(item => item.selected ? JSON.parse(JSON.stringify(item.selected)) : null).filter(Boolean)

      const priceVariations = calculatePriceVariations(selected)
      const extras = calculateExtras(selected)

      // Calculate items needed
      const defaultsCount = (comboCustomOptions.value?.defaults as any[])?.length ?? 0
      const selectedCount = selected.filter(Boolean).length
      const itemsNeeded = defaultsCount - selectedCount

      // Calculate total price factoring in quantity
      const basePrice = comboCustomOptions.value?.price as number ?? 0
      const quantity = comboStaged.value?.quantity as number ?? 1
      const total = (priceVariations + basePrice + extras) * quantity

      return {
        selected,
        itemsNeeded,
        total,
      }
    }
    catch (error) {
      log.error('Error processing custom combo options:', error)
      return {
        selected: [],
        itemsNeeded: 0,
        total: 0,
      }
    }
  })

  // Auto-update comboCustomOptions when processedOptions changes
  watch(processedComboOptions, (newValue) => {
    if (comboCustomOptions.value) {
      comboCustomOptions.value.selected = newValue.selected
      comboCustomOptions.value.itemsNeeded = newValue.itemsNeeded
      comboCustomOptions.value.total = newValue.total
    }
  }, { immediate: true })

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  function calculatePriceVariations(selected: any[]): number {
    return selected.reduce((acc, item) => {
      if (item && typeof item.price_variation !== 'undefined') {
        return acc + Number(item.price_variation)
      }
      return acc
    }, 0)
  }

  function calculateExtras(selected: any[]): number {
    return selected.reduce((acc, item) => {
      let itemTotal = 0

      // Unified modifiers (now always arrays)
      if (item?.selected_modifiers) {
        Object.values(item.selected_modifiers).forEach((selections) => {
          if (Array.isArray(selections)) {
            selections.forEach((selection: any) => {
              if (selection?.price_modifier && typeof selection.price_modifier === 'number') {
                // Multiply modifier price by item quantity for custom combos
                itemTotal += selection.price_modifier * (item.quantity || 1)
              }
            })
          }
        })
      }

      return acc + itemTotal
    }, 0)
  }

  function extractDefaults(comboItems: any[]): any[] {
    const defaults: any[] = []

    comboItems.forEach((group) => {
      if (group.options && Array.isArray(group.options)) {
        group.options.forEach((option: any) => {
          if (option.default) {
            defaults.push({
              group_id: group.group_id,
              group_name: group.group_name,
              product: option.product_id,
              product_id: option.product_id,
              product_name: option.product_name,
              quantity: option.quantity || 1,
            })
          }
        })
      }
    })

    return defaults
  }

  // ============================================
  // METHODS
  // ============================================
  function openComboModal(combo: any): void {
    comboModal.value = true

    comboStaged.value = {
      ...JSON.parse(JSON.stringify(combo)),
      quantity: combo.quantity || 1,
      combo_id: combo.id,
      defaults: extractDefaults(combo.items),
    }

    const { generate } = useUUID()
    if (!comboStaged.value.cart_uuid) {
      comboStaged.value.cart_uuid = generate()
    }
  }

  function customize(): void {
    comboCustomized.value = true
    comboCustomOptions.value = {
      type: 'CUSTOM_COMBO',
      items: comboStaged.value?.items ?? [],
      price: comboStaged.value?.price ?? 0,
      defaults: comboStaged.value?.defaults ?? [],
      cart_uuid: comboStaged.value?.cart_uuid ?? null,
    }

    // Auto-select default products and their modifiers
    if (comboCustomOptions.value.items && Array.isArray(comboCustomOptions.value.items)) {
      comboCustomOptions.value.items = comboCustomOptions.value.items.map((group: any) => {
        // Find default product in this group
        const defaultProduct = group.options?.find((opt: any) => opt.default)
        if (defaultProduct) {
          group.selected = {
            group_id: group.group_id,
            product_id: defaultProduct.product_id,
            product_name: defaultProduct.product_name,
            quantity: defaultProduct.quantity || 1,
            price_variation: defaultProduct.price_variation || 0,
          }
        }
        return group
      })
    }
  }

  function increaseQuantity(): void {
    if (comboStaged.value.quantity) {
      (comboStaged.value.quantity as number)++
    }
  }

  function decreaseQuantity(): void {
    if (comboStaged.value.quantity && (comboStaged.value.quantity as number) > 1) {
      (comboStaged.value.quantity as number)--
    }
  }

  function validateRequiredModifiers(): boolean {
    if (!comboCustomOptions.value?.items) return true

    for (const group of comboCustomOptions.value.items as any[]) {
      if (!group.selected) continue

      const selectedItem = group.selected

      // Find the original product option to get its modifiers
      const originalOption = group.options?.find((opt: any) => opt.product_id === selectedItem.product_id)
      if (!originalOption?.product_modifiers) continue

      // Check if all required modifiers have selections
      for (const modifier of originalOption.product_modifiers) {
        if (modifier.is_required) {
          const hasSelection = selectedItem.selected_modifiers?.[modifier.id]
            && Array.isArray(selectedItem.selected_modifiers[modifier.id])
            && selectedItem.selected_modifiers[modifier.id].length > 0

          if (!hasSelection) {
            return false
          }
        }
      }
    }

    return true
  }

  function reset(): void {
    comboStaged.value = {}
    comboModal.value = false
    comboCustomized.value = false
    comboCustomOptions.value = null
  }

  // ============================================
  // PUBLIC API
  // ============================================
  return {
    // State
    comboStaged,
    comboModal,
    comboCustomized,
    comboCustomOptions,

    // Computed
    comboStagedTotal,
    processedComboOptions,

    // Methods
    openComboModal,
    customize,
    increaseQuantity,
    decreaseQuantity,
    validateRequiredModifiers,
    reset,
  }
})
